import { useEffect, useState } from "react";
import { AppState, NativeModules, Platform } from "@-/com.npmjs.react-native";
import { applicationId } from "@-/com.npmjs.expo-application";
import { nativeDevBuildOverlay } from "@-/dev.improve.haniverse.modules.dev-build-overlay";
import {
  getNativeBuildStatus,
  readLocalNativeBuild,
  type LocalNativeBuild,
} from "#native-build-status";

/** Follow the actual bundle's Metro origin, including agent ports and remote tunnels. */
function latestBuildUrl(): string | null {
  const source: { getConstants?: () => { scriptURL?: unknown }; scriptURL?: unknown } | undefined =
    NativeModules.SourceCode;
  const scriptUrl = source?.getConstants?.().scriptURL ?? source?.scriptURL;
  if (typeof scriptUrl !== "string" || applicationId === null) {
    return null;
  }
  try {
    const url = new URL("/__haniverse/local-native-build", scriptUrl);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return null;
    }
    url.searchParams.set("bundleIdentifier", applicationId);
    return url.href;
  } catch {
    return null;
  }
}

export function useNativeBuildStatus() {
  const [latest, setLatest] = useState<LocalNativeBuild | null>(null);
  useEffect(() => {
    if (!__DEV__ || Platform.OS !== "ios") {
      return;
    }
    let disposed = false;
    let request: AbortController | null = null;
    const check = async () => {
      if (request !== null || AppState.currentState !== "active") {
        return;
      }
      const url = latestBuildUrl();
      if (url === null || applicationId === null) {
        return;
      }
      const controller = new AbortController();
      request = controller;
      const timeout = setTimeout(() => {
        controller.abort();
      }, 5000);
      try {
        const response = await fetch(url, { cache: "no-store", signal: controller.signal });
        const next = response.ok
          ? readLocalNativeBuild(await response.json(), applicationId)
          : null;
        if (!disposed) {
          setLatest(next);
        }
      } catch {
        if (!disposed) {
          setLatest(null);
        }
      } finally {
        clearTimeout(timeout);
        request = null;
      }
    };
    void check();
    const interval = setInterval(() => {
      void check();
    }, 10_000);
    const subscription = AppState.addEventListener("change", (state) => {
      if (state === "active") {
        void check();
      }
    });
    return () => {
      disposed = true;
      clearInterval(interval);
      subscription.remove();
      request?.abort();
    };
  }, []);
  return Platform.OS === "ios"
    ? getNativeBuildStatus(nativeDevBuildOverlay?.buildId ?? null, latest)
    : { label: "", outdated: false, text: "" };
}
